import * as z from 'zod/mini';

import { DEPOSIT_MODES, WALLET_ACCOUNT_TYPES } from '@/constants/deposit';

const AMOUNT = z.string().check(z.regex(/^-?\d+(\.\d+)?$/));
const ISO_DATE = z.string().check(z.minLength(1));
const DECIMAL_PLACES = z._default(z.number().check(z.int(), z.gte(0), z.lte(8)), 2);

/**
 * Unknown statuses are kept as strings, not coerced. The state machine renders the neutral screen
 * rather than guessing (I-7). `.catch('initiated')` would silently lie about money.
 */
export const depositStatusSchema = z.string().check(z.minLength(1));

export const portalDepositSchema = z.object({
  id: z.string(),
  publicId: z.string(),
  merchantId: z.string(),
  traderId: z.nullable(z.string()),
  playerRef: z.string(),
  status: depositStatusSchema,
  mode: z.nullable(z.enum(DEPOSIT_MODES)),
  amount: AMOUNT,
  receivedAmount: z.nullable(AMOUNT),
  currency: z.string().check(z.minLength(1)),
  decimalPlaces: DECIMAL_PLACES,
  merchantOrderId: z.nullable(z.string()),
  paymentMethodCode: z.nullable(z.string()),
  providerReference: z.nullable(z.string()),
  createdAt: ISO_DATE,
  expiresAt: z.nullable(ISO_DATE),
  verificationExpiresAt: z.nullable(ISO_DATE),
  updatedAt: ISO_DATE,
  // Omitted on older payloads; null when the merchant did not supply one.
  returnUrl: z._default(z.nullable(z.string()), null),
});

export type PortalDeposit = z.infer<typeof portalDepositSchema>;

const themeSchema = z.object({
  primary: z.string(),
  secondary: z.string(),
  background: z.string(),
  text: z.string(),
});

const schemeAssetsSchema = z.object({
  logoUrl: z.nullable(z.string()),
  faviconUrl: z.nullable(z.string()),
  theme: themeSchema,
});

export const resolvedBrandingSchema = z.object({
  displayName: z.string(),
  colorScheme: z.enum(['system', 'light', 'dark']),
  customDomain: z.nullable(z.string()),
  supportEmail: z.nullable(z.string()),
  supportPhone: z.nullable(z.string()),
  footerText: z.nullable(z.string()),
  light: schemeAssetsSchema,
  dark: schemeAssetsSchema,
});

export type ResolvedBranding = z.infer<typeof resolvedBrandingSchema>;

export const walletInstructionsSchema = z.object({
  accountType: z.enum(WALLET_ACCOUNT_TYPES),
  accountLabel: z.string(),
  accountNumber: z.string(),
  bankCode: z.nullable(z.string()),
  accountHolderName: z.nullable(z.string()),
  qrImageUrl: z.nullable(z.string()),
});

export type WalletInstructions = z.infer<typeof walletInstructionsSchema>;

export const cashierSessionSchema = z.object({
  ...portalDepositSchema.shape,
  branding: resolvedBrandingSchema,
  walletInstructions: z.nullable(walletInstructionsSchema),
});

export type CashierSession = z.infer<typeof cashierSessionSchema>;
